import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PeopleAltRounded from "@mui/icons-material/PeopleAltRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCookies } from "react-cookie";
import Schedule from "../../api/Schedule";
import Header from "../Header";
import ContentAreaWrapper from "../ContentAreaWrapper";
import Booking from "../../api/Booking";
import { parseWithOffset } from "../../utils/dayjsUtils";
import dayjs from "dayjs";
import ClientsReviewItem from "./ClientsReviewItem";
import TabHeader from "../TabHeader";
import BottomButton from "../BottomButton";
import StatusScreen from "../StatusScreen";
import { FONTS } from "../../constants/styles";


const ClientsReview = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);

    const dateString = params.get("date"); // Format: YYYY-MM-DD
    const [bookings, setBookings] = useState<
        { booking: Booking; user: any; service: any; start: dayjs.Dayjs }[]
    >([]);
    const [discounts, setDiscounts] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(true);

    const uuid = cookies.uuid;
    const brand_id = cookies.brand_id;
    const location_id = cookies.location_id;

    const isPastDate =
        dateString && dayjs(dateString).isBefore(dayjs().startOf("day"));

    useEffect(() => {
        if (!uuid || !brand_id || !location_id || !dateString) {
            navigate("/");
            return;
        }

        if (isPastDate) {
            setLoading(false);
            return;
        }

        const [year, month, day] = dateString.split("-").map(Number);

        const fetchSchedule = async () => {
            try {
                const res = await Schedule.scheduleDay(
                    uuid,
                    brand_id,
                    location_id,
                    year,
                    month,
                    day
                );

                const slots = res?.slots;

                const combinedSlots = (slots || []).map(
                    (slot: any) => ({
                        value: {
                            key: slot.key,
                            start: parseWithOffset(slot.start),
                            end: parseWithOffset(slot.end),
                            ...(slot.type === "book" ? slot : {}),
                        },
                        type: slot.type,
                    })
                );

                const bookedItems = combinedSlots
                    .filter(
                        (item): item is { value: Booking; type: "book" } =>
                            item.type === "book"
                    )
                    .map((item) => ({
                        booking: item.value,
                        start: parseWithOffset(item.value.start),
                        end: parseWithOffset(item.value.end),
                        user: item.value.user,
                        service: item.value.service,
                    }));

                setBookings(bookedItems);
            } catch (error) {
                console.error("Error fetching schedule:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSchedule();
    }, [uuid, brand_id, location_id, dateString, isPastDate, navigate]);

    const storeClientReviewAndContinue = () => {
        if (!dateString) return;

        const payload = {
            date: dateString,
            bookings: bookings.map((b) => ({
                booking_key: b.booking.key,
                user_key: b.user,
                discount: discounts[b.booking.key] ?? 0,
            })),
        };

        sessionStorage.setItem("freeMyDay", JSON.stringify(payload));
        navigate("/aiadmin/free-my-day/reason");
    };

    if (isPastDate) {
        return (
            <StatusScreen
                icon={HistoryRounded}
                iconColor={primary}
                title="Time Travel Not Supported"
                subtitle="You can only free upcoming days. This date has already passed."
                description={`Selected: ${dayjs(dateString).format("MMMM D, YYYY")}`}
                buttonLabel="Back to Calendar"
                onButtonClick={() => navigate("/calendar")}
            />
        );
    }

    return (
        <>
            <Header />
            <TabHeader
                name="Affected Clients"
                onClick={() => navigate("/calendar")}
            />
            <ContentAreaWrapper headerType="tab" noFooter stickyBottomButton>
                <Stack spacing={1.5} width="100%">
                    {/* Section header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "10px",
                                bgcolor: alpha(primary, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "0 0 auto",
                                color: primary,
                            }}
                        >
                            <PeopleAltRounded sx={{ fontSize: 18 }} />
                        </Box>
                        <Stack>
                            <Typography
                                fontFamily={FONTS.display}
                                fontSize="15px"
                                fontWeight={600}
                            >
                                {loading
                                    ? "Loading…"
                                    : bookings.length === 0
                                      ? "No bookings found"
                                      : `${bookings.length} booking${bookings.length !== 1 ? "s" : ""} affected`}
                            </Typography>
                            <Typography
                                fontSize="12px"
                                color="text.secondary"
                            >
                                {bookings.length > 0
                                    ? "Set individual discounts per client"
                                    : "\u00A0"}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Booking cards */}
                    <Stack spacing={1.5}>
                        {bookings.map(
                            ({ booking, user, service, start }) => (
                                <ClientsReviewItem
                                    key={booking.key}
                                    bookingId={booking.key}
                                    userId={user}
                                    serviceId={service}
                                    start={start}
                                    currentDiscount={
                                        discounts[booking.key] ?? 0
                                    }
                                    onDiscountChange={(val) =>
                                        setDiscounts((prev) => ({
                                            ...prev,
                                            [booking.key]: val,
                                        }))
                                    }
                                />
                            )
                        )}
                    </Stack>
                </Stack>
            </ContentAreaWrapper>
            <BottomButton sticky onClick={storeClientReviewAndContinue}>
                Next
            </BottomButton>
        </>
    );
};

export default ClientsReview;
